import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaBell, FaRobot, FaChartLine, FaMobile, FaSync, FaCrown, FaCheck } from 'react-icons/fa';
import RegisterModal from '../components/RegisterModal';

// Animations
const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const floatAnimation = keyframes`
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
`;

// Styled Components
const PremiumSection = styled.section`
    min-height: 100vh;
    background: linear-gradient(135deg, #0c0c0c 0%, #1a1a1a 50%, #2d2d2d 100%);
    padding: 100px 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background:
                radial-gradient(circle at 10% 20%, rgba(245, 102, 7, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 90% 80%, rgba(245, 102, 7, 0.05) 0%, transparent 50%);
    }
`;

const Container = styled.div`
    max-width: 1400px;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 80px;
    align-items: center;
    position: relative;
    z-index: 2;

    @media (max-width: 1200px) {
        grid-template-columns: 1fr;
        gap: 60px;
        text-align: center;
    }
`;

const ContentSection = styled.div`
    animation: ${fadeInUp} 1s ease-out;
`;

const Badge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: linear-gradient(135deg, #f56607 0%, #ff8c00 100%);
    color: white;
    padding: 12px 24px;
    border-radius: 25px;
    font-weight: 600;
    font-size: 0.9em;
    margin-bottom: 30px;
`;

const Title = styled.h1`
    font-size: 3.5em;
    font-weight: 800;
    color: #ffffff;
    margin-bottom: 30px;
    line-height: 1.1;
    background: linear-gradient(135deg, #ffffff 0%, #f56607 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (max-width: 768px) {
        font-size: 2.5em;
    }
`;

const Subtitle = styled.p`
    font-size: 1.3em;
    color: #b0b0b0;
    line-height: 1.6;
    margin-bottom: 50px;
    max-width: 500px;

    @media (max-width: 1200px) {
        margin: 0 auto 50px;
    }
`;

const FeaturesGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 25px;
    margin-bottom: 50px;
    max-width: 500px;
`;

const FeatureCard = styled.div`
    display: flex;
    align-items: flex-start;
    padding: 25px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
        transform: translateX(10px);
        background: rgba(245, 102, 7, 0.1);
        border-color: rgba(245, 102, 7, 0.3);
    }
`;

const FeatureIcon = styled.div`
    width: 50px;
    height: 50px;
    border-radius: 12px;
    background: rgba(245, 102, 7, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 20px;
    flex-shrink: 0;
    color: #f56607;
    font-size: 1.3em;
`;

const FeatureContent = styled.div`
    flex: 1;
`;

const FeatureTitle = styled.h3`
    font-size: 1.2em;
    color: #ffffff;
    margin-bottom: 8px;
    font-weight: 600;
`;

const FeatureDescription = styled.p`
    color: #b0b0b0;
    line-height: 1.5;
    font-size: 0.95em;
`;

const CTAButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 15px;
    padding: 20px 40px;
    background: linear-gradient(135deg, #f56607 0%, #ff8c00 100%);
    color: white;
    border: none;
    border-radius: 15px;
    font-size: 1.2em;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 10px 30px rgba(245, 102, 7, 0.4);

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 15px 40px rgba(245, 102, 7, 0.6);
    }
`;

const ScreenshotSection = styled.div`
    animation: ${fadeInUp} 1s ease-out 0.3s both;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const ScreenshotContainer = styled.div`
    position: relative;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    max-width: 600px;
`;

const ScreenshotCard = styled.div`
    position: relative;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
    transition: all 0.3s ease;
    animation: ${floatAnimation} 6s ease-in-out infinite;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    cursor: pointer;

    &:nth-child(1) {
        animation-delay: 0s;
        transform: rotate(-5deg);
    }
    &:nth-child(2) {
        animation-delay: 1s;
        transform: rotate(3deg);
    }
    &:nth-child(3) {
        animation-delay: 2s;
        transform: rotate(-2deg);
    }
    &:nth-child(4) {
        animation-delay: 3s;
        transform: rotate(4deg);
    }

    &:hover {
        transform: scale(1.05) rotate(0deg) !important;
        z-index: 10;
        box-shadow: 0 30px 60px rgba(245, 102, 7, 0.3);
    }
`;

const ScreenshotImage = styled.img`
    width: 100%;
    height: auto;
    display: block;
    border-radius: 20px;
`;

const ScreenshotOverlay = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
    padding: 20px;
    color: white;
    font-weight: 600;
    font-size: 0.9em;
`;

const Modal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalImage = styled.img`
    max-width: 90%;
    max-height: 90%;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 20px;
    right: 20px;
    background: none;
    border: none;
    color: white;
    font-size: 2em;
    cursor: pointer;
`;

const StatsContainer = styled.div`
    display: flex;
    gap: 40px;
    margin-top: 50px;
    flex-wrap: wrap;
    justify-content: flex-start;

    @media (max-width: 1200px) {
        justify-content: center;
    }
`;

const StatItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    @media (max-width: 1200px) {
        align-items: center;
    }
`;

const StatNumber = styled.div`
    font-size: 2em;
    font-weight: 800;
    color: #f56607;
    margin-bottom: 5px;
`;

const StatLabel = styled.div`
    color: #b0b0b0;
    font-size: 0.9em;
    font-weight: 500;
`;

const PremiumMembership: React.FC = () => {
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);

    const features = [
        {
            icon: <FaBell />,
            title: "Okamžité notifikácie",
            description: "S našou aplikáciou nezmeškáte ani jediný tip"
        },
        {
            icon: <FaChartLine />,
            title: "Polčasové hodnotenie nášho zápasu na tikete",
            description: "Naše rady a informácie priamo počas zápasu"
        },
        {
            icon: <FaMobile />,
            title: "Multiplatformný prístup",
            description: "Plná funkcionalita v aplikácii aj na webe"
        },
        {
            icon: <FaSync />,
            title: "Bet buildery, krátkodobé tipy a dlhodobé tipy",
            description: "Viackrát do týždňa"
        },
        {
            icon: <FaCrown />,
            title: "Týždenne Podcasty",
            description: "Rozobranie uplynulého víkendu a príprava na nasledujúci. Nová epizóda každý štvrtok alebo piatok."
        }
    ];

    const handleJoinClick = () => {
        setIsRegisterModalOpen(true);
    };

    const handleCloseRegisterModal = () => {
        setIsRegisterModalOpen(false);
    };

    const handleRegisterSuccess = () => {
        console.log('Registration successful from PremiumMembership!');
        // You can add any post-registration logic here
        // For example: redirect to payment page, show success message, etc.
    };

    return (
        <PremiumSection>
            <Container>
                <ContentSection>
                    <Badge>
                        <FaCrown />
                        Prémiové Členstvo
                    </Badge>

                    <Title>
                        Posuňte svoju úroveň
                    </Title>

                    <Subtitle>
                        Odomknite si kompletný náš obsah a nenechávajte svoje ťažko zarobené korunky na náhodu 🍀
                    </Subtitle>

                    <FeaturesGrid>
                        {features.map((feature, index) => (
                            <FeatureCard key={index}>
                                <FeatureIcon>
                                    {feature.icon}
                                </FeatureIcon>
                                <FeatureContent>
                                    <FeatureTitle>{feature.title}</FeatureTitle>
                                    <FeatureDescription>{feature.description}</FeatureDescription>
                                </FeatureContent>
                            </FeatureCard>
                        ))}
                    </FeaturesGrid>

                    <CTAButton onClick={handleJoinClick}>
                        <FaCheck />
                        Začať s prémiovým členstvom
                    </CTAButton>

                    <StatsContainer>
                        <StatItem>
                            <StatNumber>Viac ako 15 rokov skúseností.</StatNumber>
                            <StatLabel></StatLabel>
                        </StatItem>
                        <StatItem>
                            <StatNumber>Vždy odvedieme maximum, čo je v našich silách!</StatNumber>
                            <StatLabel></StatLabel>
                        </StatItem>
                    </StatsContainer>
                </ContentSection>

                <ScreenshotSection>
                    <ScreenshotContainer>
                        <ScreenshotCard onClick={() => setSelectedImage('/1.PNG')}>
                            <ScreenshotImage
                                src="/1.PNG"
                                alt="Notifikácie - Okamžité upozornenia"
                                onError={(e) => {
                                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjYwMCIgdmlld0JveD0iMCAwIDMwMCA2MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iNjAwIiBmaWxsPSIjMkQyRDJEIi8+Cjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjRjU2NjA3IiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiPk5vdGlmaWthY2llPC90ZXh0Pgo8L3N2Zz4K'
                                }}
                            />
                            <ScreenshotOverlay>Analyzované tikety</ScreenshotOverlay>
                        </ScreenshotCard>

                        <ScreenshotCard onClick={() => setSelectedImage('/2.PNG')}>
                            <ScreenshotImage
                                src="/2.PNG"
                                alt="Analýzy - AI vylepšené predpovede"
                            />
                            <ScreenshotOverlay>Týždenné Podcasty</ScreenshotOverlay>
                        </ScreenshotCard>

                        <ScreenshotCard onClick={() => setSelectedImage('/3.PNG')}>
                            <ScreenshotImage
                                src="/3.PNG"
                                alt="Dashboard - Prehľad výkonnosti"
                            />
                            <ScreenshotOverlay>Diskusné fórum</ScreenshotOverlay>
                        </ScreenshotCard>

                        <ScreenshotCard onClick={() => setSelectedImage('/4.PNG')}>
                            <ScreenshotImage
                                src="/4.PNG"
                                alt="Stratégie - Pokročilé nástroje"
                            />
                            <ScreenshotOverlay>Aktívna Komunita</ScreenshotOverlay>
                        </ScreenshotCard>
                    </ScreenshotContainer>
                </ScreenshotSection>
            </Container>

            {/* Image Modal */}
            {selectedImage && (
                <Modal onClick={() => setSelectedImage(null)}>
                    <ModalImage src={selectedImage} alt="Enlarged screenshot" />
                    <CloseButton onClick={() => setSelectedImage(null)}>×</CloseButton>
                </Modal>
            )}

            {/* Register Modal */}
            <RegisterModal
                isOpen={isRegisterModalOpen}
                onRequestClose={handleCloseRegisterModal}
                onRegister={handleRegisterSuccess}
            />
        </PremiumSection>
    );
};

export default PremiumMembership;